// Спящий агент (deus_spyaschiy_agent, special, cost 5).
//
// А.4.4.4-fix1 — новая механика «вариант А»:
//   1) Ф2 хода N: розыгрыш со стенда. Случайная пустая ячейка стороны противника.
//      sleeperState = 'planted'. revealed=true (виден сразу).
//   2) Ф3 хода N: ничего не делает (никаких атак). Просто стоит.
//   3) Ф4 хода N (фаза владельца): UI показывает спящего как кликабельного.
//      Клик → armSpy → sleeperState='armed'. Если игрок не активировал — остаётся
//      'planted' до следующего Ф4 владельца.
//   4) Ф3 следующего хода: armed-спящий ПЕРВЫМ в очереди событий Ф3 (до Действий,
//      Приказов и обычных авто-атак):
//        - 3 урона Герою противника (Авангард игнорируется; обычная броня поглощает).
//        - 2 урона случайному юниту противника.
//        - 2 урона ещё одному случайному (другому) юниту противника.
//        - НЕМЕДЛЕННО переезжает на 1-ю линию владельца, первый пустой слот слева.
//          Если 1-я забита — 2-я. Если и там забито — в сброс (без deathrattle/Сины).
//        - sleeperState = null. Обычная авто-атака в этом Ф3 не делается.
//      Со следующего хода ведёт себя как обычный юнит на стороне владельца.
//
// Авангард: пока sleeperState != null — спящий НЕ считается ни для какой стороны.
// После возврата (sleeperState=null, side='owner') — обычный юнит, держит Авангард.
//
// Уязвимость пока спит у врага:
//   - Same-faction авто-атака: цель не выбирается (фильтр в priorities.js).
//   - AoE / random-юнит / targeted-Действие противника: попадает под удар как обычный юнит.
//   - Если умирает у врага: обычная смерть, БЕЗ deathrattle/Сины (по лору «его не было»).
//     Реализация: sina/Аналитик-deathrattle/Дрон-on-damage пропускают карты с sleeperState.

import { applyDamage } from '../../combat.js';
import { applyHeroDamage, redirectToSekretar } from '../../hero-damage.js';
import { onUnitSummoned, dispatchUnitDied } from './hooks.js';
import { Card } from '../../card.js';
import { getFactionCards } from '../../../data/decks.js';
import { chargeInfluence } from './influence.js';

// Этап 26: ID Спящего агента в v6 переименован на deus_inf_spyashy_agent.
// C.1 Этап 33: cost синхронизирован с xlsx v6 (был хардкод 5, в xlsx 8).
// Иначе UI показывал cost 8 (из cards-deus.js), а canPlaySpy пускал при 5 —
// игрок видел «8» но мог сыграть с 5 Влияния, а другие карты Влияния
// казались недоступными.
export const CARD_ID = 'deus_inf_spyashy_agent';
export const COST = 8;

export function canPlaySpy(state, playerIdx) {
  const p = state.players[playerIdx];
  if (p.influence < COST) return { ok: false, reason: 'not_enough_influence' };
  const enemy = state.players[1 - playerIdx];
  if (!enemy.hasFreeSlot()) return { ok: false, reason: 'no_slot_at_opponent' };
  return { ok: true };
}

// Розыгрыш Спящего: место — случайно у врага. sleeperState='planted'. revealed=true (виден).
export function playSpy(state, playerIdx, existingCard = null) {
  const can = canPlaySpy(state, playerIdx);
  if (!can.ok) return can;
  const p = state.players[playerIdx];
  const enemy = state.players[1 - playerIdx];
  if (!chargeInfluence(state, playerIdx, COST)) return { ok: false, reason: 'not_enough_influence' };
  const tpl = getFactionCards(p.hero.faction).find(c => c.id === CARD_ID);
  const spy = existingCard || new Card(tpl);
  spy.side = 'opponent';
  spy.sleeperState = 'planted';
  spy.placedTurn = state.currentTurn;
  spy.revealed = true;
  // Случайная пустая ячейка стороны противника.
  const slots = enemy.freeSlots();
  if (slots.length === 0) return { ok: false, reason: 'no_slot_at_opponent' };
  const pick = slots[state.rng.int(slots.length)];
  enemy.placeCard(spy, pick.line, pick.slot);
  spy.ownerId = p.id;                        // placeCard перетёр — возвращаем
  state.log.add(`🕵 ${p.name}: Спящий агент проникает на сторону противника (${pick.line === 0 ? '1-я' : '2-я'} линия, слот ${pick.slot + 1}) — спит до активации`);
  // onUnitSummoned для бенефициаров владельца (Ликвидатор-эксперт, аура Второго директора).
  onUnitSummoned(state, playerIdx, spy);
  return { ok: true, line: pick.line, slot: pick.slot };
}

// Активация Спящего в Ф4 владельца. spyCard — конкретный экземпляр.
export function armSpy(state, playerIdx, spyCard) {
  if (!spyCard || spyCard.id !== CARD_ID) return { ok: false, reason: 'not_a_spy' };
  if (spyCard.ownerId !== playerIdx) return { ok: false, reason: 'not_owner' };
  if (spyCard.sleeperState !== 'planted') return { ok: false, reason: 'not_planted' };
  spyCard.sleeperState = 'armed';
  state.log.add(`🕵 ${state.players[playerIdx].name}: Спящий агент активирован — ударит первым в начале следующего хода`);
  return { ok: true };
}

// Список спящих, готовых к активации игроком (planted, владелец = playerIdx).
export function getActivatableSpies(state, playerIdx) {
  const out = [];
  for (const p of state.players) {
    for (const u of p.unitsOnField()) {
      if (u.id !== CARD_ID) continue;
      if (u.sleeperState !== 'planted') continue;
      if (u.ownerId !== playerIdx) continue;
      out.push(u);
    }
  }
  return out;
}

// Удар армированного Спящего — ПЕРВЫМ в Ф3.
// Эффект: 3 урона Герою противника, 2+2 двум случайным юнитам, перемещение к владельцу.
// Возвращает true, если хоть один armed сработал — для оркестрации в phases.runPhase3.
export async function runArmedSpyAttacks(state, anim) {
  const spies = [];
  for (const p of state.players) {
    for (const u of p.unitsOnField()) {
      if (u.id === CARD_ID && u.sleeperState === 'armed' && u.isAlive()) {
        spies.push(u);
      }
    }
  }
  for (const spy of spies) {
    if (!spy.isAlive()) continue;
    if (state.players[0].hero.hp <= 0 || state.players[1].hero.hp <= 0) break;
    const ownerIdx = spy.ownerId;
    const opponentIdx = 1 - ownerIdx;
    const opponent = state.players[opponentIdx];
    state.log.add(`🕵 Спящий агент [${state.players[ownerIdx].name}] срабатывает (на стороне ${opponent.name})`);
    // 3 урона Герою противника. Секретарь перенаправляет, Парашют/Иммунитет учитываются через applyHeroDamage.
    const sek = redirectToSekretar(opponent, opponent.hero);
    if (sek.redirectedToSekretar) {
      const before = { hp: sek.target.hp, armor: sek.target.armor };
      applyDamage(sek.target, 3, { pierce: false });
      state.log.add(`↪ Перенаправлено на Секретаря: 3 урона (HP ${before.hp}→${sek.target.hp}, броня ${before.armor}→${sek.target.armor})`);
      if (sek.target.hp <= 0) {
        opponent.removeFromField(sek.target);
        state.log.add(`✗ ${sek.target.name_ru} [${opponent.name}] погибает`);
        dispatchUnitDied(state, { ownerIdx: opponent.id, card: sek.target, killer: spy, killerIdx: ownerIdx, killerWasCounter: false });
      }
    } else {
      applyHeroDamage(state, opponentIdx, 3, { pierce: false });
      state.log.add(`🕵 Спящий → Герой [${opponent.name}]: 3 урона (HP=${opponent.hero.hp}, броня=${opponent.hero.armor})`);
    }
    if (opponent.hero.hp <= 0) {
      state.log.add(`💀 ${opponent.hero.name_ru} [${opponent.name}] пал`);
      // Спящий не возвращается — матч заканчивается.
      spy.sleeperState = null;
      break;
    }
    // 2+2 двум случайным юнитам противника. Исключаем самого spy и других spy того же владельца.
    const candidates = opponent.unitsOnField().filter(u => u !== spy && u.ownerId !== ownerIdx);
    const picks = pickN(candidates, 2, state.rng);
    for (const target of picks) {
      const before = { hp: target.hp, armor: target.armor };
      applyDamage(target, 2, { pierce: false });
      state.log.add(`🕵 Спящий → ${target.name_ru} [${opponent.name}]: 2 урона (HP ${before.hp}→${target.hp}, броня ${before.armor}→${target.armor})`);
      if (target.hp <= 0) {
        opponent.removeFromField(target);
        state.log.add(`✗ ${target.name_ru} [${opponent.name}] погибает`);
        dispatchUnitDied(state, { ownerIdx: opponent.id, card: target, killer: spy, killerIdx: ownerIdx, killerWasCounter: false });
      }
    }
    // Немедленно переезжаем на 1-ю линию владельца (или 2-ю, или сброс).
    moveSpyToOwnerSide(state, spy);
    spy.sleeperState = null;
    await anim?.pause?.();
  }
}

// Перемещение Спящего на сторону владельца. Сначала 1-я линия (слева), затем 2-я.
// Если оба ряда заняты — в сброс владельца, без триггеров.
function moveSpyToOwnerSide(state, spy) {
  const opponent = state.players[1 - spy.ownerId];
  const owner = state.players[spy.ownerId];
  // Снять с поля противника.
  if (spy.line !== null && spy.slot !== null) {
    if (opponent.field[spy.line]?.[spy.slot] === spy) {
      opponent.field[spy.line][spy.slot] = null;
    }
  }
  spy.line = null;
  spy.slot = null;
  spy.side = 'owner';
  for (let line = 0; line < 2; line++) {
    for (let slot = 0; slot < 4; slot++) {
      if (owner.field[line][slot] === null) {
        owner.placeCard(spy, line, slot);
        spy.ownerId = owner.id;
        spy.placedTurn = state.currentTurn;
        spy.revealed = true;
        state.log.add(`🕵 Спящий агент перемещается к ${owner.name} (${line === 0 ? '1-я' : '2-я'} линия, слот ${slot + 1})`);
        return;
      }
    }
  }
  // Нет места.
  spy.alive = false;
  owner.discard.push(spy);
  state.log.add(`🕵 Спящий агент: у [${owner.name}] нет места — уходит в сброс`);
}

function pickN(arr, n, rng) {
  const pool = [...arr];
  const out = [];
  for (let i = 0; i < n && pool.length > 0; i++) {
    const idx = rng.int(pool.length);
    out.push(pool.splice(idx, 1)[0]);
  }
  return out;
}
